(() => {
  'use strict';

  const root = document.documentElement;
  const main = document.querySelector('#MainContent');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!main) return;

  root.classList.add('aldren-motion-ready');

  const unique = (nodes) => [...new Set(nodes)].filter((node) => node instanceof HTMLElement);

  const getSections = (scope = main) => {
    const directSections = scope.querySelectorAll(':scope > .shopify-section');
    if (directSections.length) return [...directSections];

    return [...scope.querySelectorAll('.shopify-section')].filter(
      (section) => !section.parentElement?.closest('.shopify-section')
    );
  };

  let observer;
  if (!reducedMotion && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('aldren-is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  }

  const reveal = (element) => {
    if (!(element instanceof HTMLElement)) return;
    element.classList.add('aldren-reveal');

    if (reducedMotion || !observer || window.Shopify?.designMode) {
      element.classList.add('aldren-is-visible');
      return;
    }

    observer.observe(element);
  };

  const enhanceScope = (scope = main) => {
    const sections = getSections(scope);
    const firstSection = getSections(main)[0];

    sections.forEach((section) => {
      if (section !== firstSection) reveal(section);
    });

    if (firstSection && (scope === main || scope.contains(firstSection))) {
      const heroCopy = unique([
        ...firstSection.querySelectorAll('h1, h2, [class*="heading"], .button, a[href]')
      ]).slice(0, 6);

      heroCopy.forEach((element, index) => {
        element.classList.add('aldren-hero-copy');
        element.style.setProperty('--aldren-motion-delay', `${140 + index * 90}ms`);
      });

      const heroImage = firstSection.querySelector('picture img, [class*="image"] img, img');
      if (heroImage instanceof HTMLElement) {
        heroImage.classList.add('aldren-hero-media');
        heroImage.parentElement?.classList.add('aldren-hero-media-frame');
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          heroCopy.forEach((element) => element.classList.add('aldren-is-visible'));
        });
      });
    }

    const cards = unique([
      ...scope.querySelectorAll('product-card, .product-card, .card-product, [data-product-card]')
    ]);

    cards.forEach((card, index) => {
      card.classList.add('aldren-product-card');
      if (!card.classList.contains('aldren-reveal')) reveal(card);
      card.style.setProperty('--aldren-motion-delay', `${Math.min(index % 4, 3) * 75}ms`);
    });

    unique([
      ...scope.querySelectorAll(
        'a.button, button.button, .shopify-payment-button__button, [name="add"], button[type="submit"]'
      )
    ]).forEach((button) => button.classList.add('aldren-luxe-button'));
  };

  const setupMobilePurchaseBar = () => {
    if (!String(main.dataset.template || '').startsWith('product')) return;
    if (document.querySelector('.aldren-mobile-cta')) return;

    const selectors = [
      'product-form [name="add"]',
      '.product-form__submit',
      '[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]'
    ];
    const sourceButton = selectors.map((selector) => main.querySelector(selector)).find(Boolean);
    if (!(sourceButton instanceof HTMLButtonElement)) return;

    const bar = document.createElement('div');
    bar.className = 'aldren-mobile-cta';
    bar.setAttribute('aria-label', 'Quick purchase');

    const title = document.createElement('p');
    title.className = 'aldren-mobile-cta__title';
    title.textContent = main.querySelector('h1')?.textContent?.trim() || 'Aldren Signature';

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'aldren-mobile-cta__button aldren-luxe-button';

    const syncButton = () => {
      action.disabled = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';
      action.textContent = sourceButton.textContent?.trim() || 'Add to cart';
    };

    syncButton();
    bar.append(title, action);
    document.body.append(bar);

    action.addEventListener('click', () => {
      if (sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true') {
        sourceButton.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        sourceButton.focus({ preventScroll: true });
        return;
      }
      sourceButton.click();
    });

    new MutationObserver(syncButton).observe(sourceButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true
    });

    let sourceVisible = true;
    const updateVisibility = () => {
      const shouldShow = !sourceVisible && window.scrollY > 180;
      bar.classList.toggle('aldren-is-visible', shouldShow);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          sourceVisible = entry.isIntersecting;
          updateVisibility();
        },
        { threshold: 0.15 }
      ).observe(sourceButton);
    }

    let visibilityFrame = 0;
    const requestVisibilityUpdate = () => {
      if (visibilityFrame) return;
      visibilityFrame = requestAnimationFrame(() => {
        visibilityFrame = 0;
        updateVisibility();
      });
    };

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
  };

  enhanceScope();
  setupMobilePurchaseBar();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) enhanceScope(event.target);
  });
})();
